import React, { useEffect, useRef, useState } from 'react';

import { use2gisMapReady } from 'hooks/use2gisMapReady';
import {
  Coordinate,
  getPriorityName,
  Priority,
  TaskCoordinate,
  TaskStatusTransitionCoordinate
} from 'types/interfaces';
import { formatDateStringFromRaw } from 'utils/date-utils';
import { loadMarkerCluster } from 'utils/loadMarkerCluster';

interface MapComponentProps {
  relevantCoordinates?: Coordinate[];
  validTasks?: TaskCoordinate[];
  statusTransitions?: TaskStatusTransitionCoordinate[];
  reservedRightWidth?: number;
  containerId?: string;
}

const EMPTY_COORDINATES: Coordinate[] = [];
const EMPTY_TASKS: TaskCoordinate[] = [];
const EMPTY_TRANSITIONS: TaskStatusTransitionCoordinate[] = [];

const createPrimeIconMarker = (color: string) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="${color}">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `;

  return window.DG.icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svg),
    iconSize: [34, 34],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

type MapSelectable =
  | Coordinate
  | TaskCoordinate
  | TaskStatusTransitionCoordinate
  | null;

const hasCoordinates = (
  transition: TaskStatusTransitionCoordinate
): transition is TaskStatusTransitionCoordinate & {
  latitude: number;
  longitude: number;
} =>
  typeof transition.latitude === 'number' &&
  typeof transition.longitude === 'number';

const MapComponent: React.FC<MapComponentProps> = ({
  relevantCoordinates = EMPTY_COORDINATES,
  validTasks = EMPTY_TASKS,
  statusTransitions = EMPTY_TRANSITIONS,
  reservedRightWidth = 320,
  containerId = 'map-container'
}) => {
  const [selectedItem, setSelectedItem] = useState<MapSelectable>(null);
  const [modalPosition, setModalPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const mapRef = useRef<any>(null);
  const employeeClusterRef = useRef<any>(null);
  const taskClusterRef = useRef<any>(null);
  const statusLayerRef = useRef<any>(null);

  const isApiReady = use2gisMapReady();

  useEffect(() => {
    if (!isApiReady) {
      return;
    }
    initializeMap();

    function initializeMap() {
      loadMarkerCluster().then(() => {
        // Clean up previous map instance
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }

        // Create new map instance
        mapRef.current = window.DG.map(containerId, {
          center: [55.67479, 37.504028],
          zoom: 13,
          key: 'b78aa276-8aa3-4a09-a608-4daeb2bb953c'
        });

        // Create separate cluster groups
        employeeClusterRef.current = window.DG.markerClusterGroup({
          iconCreateFunction: function (cluster: any) {
            return window.DG.divIcon({
              html: `<span>${cluster.getChildCount()}</span>`,
              className: 'employee-cluster',
              iconSize: window.DG.point(40, 40)
            });
          }
        });

        taskClusterRef.current = window.DG.markerClusterGroup({
          iconCreateFunction: function (cluster: any) {
            return window.DG.divIcon({
              html: `<span>${cluster.getChildCount()}</span>`,
              className: 'task-cluster',
              iconSize: window.DG.point(40, 40)
            });
          }
        });

        statusLayerRef.current = window.DG.layerGroup();

        const bounds = window.DG.latLngBounds();

        // Add employees to their cluster group
        relevantCoordinates.forEach((coord) => {
          if (coord.latitude && coord.longitude) {
            const marker = createEmployeeMarker(coord);
            employeeClusterRef.current.addLayer(marker);
            bounds.extend([coord.latitude, coord.longitude]);
          }
        });

        // Add tasks to their cluster group
        validTasks.forEach((task) => {
          if (task.asset?.latitude && task.asset?.longitude) {
            const marker = createTaskMarker(task);
            taskClusterRef.current.addLayer(marker);
            bounds.extend([task.asset.latitude, task.asset.longitude]);
          }
        });

        // Add both cluster groups to the map
        mapRef.current.addLayer(employeeClusterRef.current);
        mapRef.current.addLayer(taskClusterRef.current);

        const transitionsWithCoordinates =
          statusTransitions.filter(hasCoordinates);

        if (transitionsWithCoordinates.length > 0) {
          const orderedTransitions = [...transitionsWithCoordinates].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          );

          orderedTransitions.forEach((transition, index) => {
            const marker = createStatusTransitionMarker(transition, index + 1);
            if (marker) {
              statusLayerRef.current.addLayer(marker);
              bounds.extend([transition.latitude, transition.longitude]);
            }
          });

          if (orderedTransitions.length > 1) {
            const polyline = window.DG.polyline(
              orderedTransitions.map((transition) => [
                transition.latitude,
                transition.longitude
              ]),
              {
                color: '#1f9d55',
                weight: 3,
                dashArray: '4 6'
              }
            );
            statusLayerRef.current.addLayer(polyline);
          }

          mapRef.current.addLayer(statusLayerRef.current);
        }

        const hasBoundsData =
          relevantCoordinates.length > 0 ||
          validTasks.length > 0 ||
          transitionsWithCoordinates.length > 0;

        if (hasBoundsData) {
          mapRef.current.fitBounds(bounds);
        }
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      if (employeeClusterRef.current) {
        employeeClusterRef.current.clearLayers();
        employeeClusterRef.current = null;
      }
      if (taskClusterRef.current) {
        taskClusterRef.current.clearLayers();
        taskClusterRef.current = null;
      }
      if (statusLayerRef.current) {
        statusLayerRef.current.clearLayers();
        statusLayerRef.current = null;
      }
    };
  }, [
    relevantCoordinates,
    validTasks,
    statusTransitions,
    containerId,
    isApiReady
  ]);

  const handleMarkerClick = (
    item: Exclude<MapSelectable, null>,
    marker: any
  ) => {
    const popupElement = marker.getPopup().getElement();
    const mapContainer = document.getElementById(containerId);

    if (popupElement && mapContainer) {
      const popupRect = popupElement.getBoundingClientRect();
      const mapRect = mapContainer.getBoundingClientRect();

      const modalWidth = 400;
      const modalHeight = 300;
      const margin = 10;
      const rightBlockWidth = reservedRightWidth;

      // Calculate available spaces considering right block
      const availableMapWidth = mapRect.width - rightBlockWidth;
      const popupLeft = popupRect.left - mapRect.left;
      const popupRight = popupRect.right - mapRect.left;
      const popupTop = popupRect.top - mapRect.top;
      const popupBottom = popupRect.bottom - mapRect.top;

      // Calculate safe zones around popup
      const popupSafeZone = {
        left: popupLeft - modalWidth - margin,
        right: popupRight + margin,
        top: popupTop - modalHeight - margin,
        bottom: popupBottom + margin
      };

      // Try positions in this order: right, left, bottom, top
      let top = 0;
      let left = 0;

      // 1. Try right side
      if (popupSafeZone.right + modalWidth <= availableMapWidth) {
        left = popupSafeZone.right;
        top = popupTop;
      }
      // 2. Try left side
      else if (popupSafeZone.left >= 0) {
        left = popupSafeZone.left;
        top = popupTop;
      }
      // 3. Try below
      else if (popupSafeZone.bottom + modalHeight <= mapRect.height) {
        left = Math.max(
          0,
          Math.min(
            popupLeft + popupRect.width / 2 - modalWidth / 2,
            availableMapWidth - modalWidth
          )
        );
        top = popupSafeZone.bottom;
      }
      // 4. Try above
      else if (popupSafeZone.top >= 0) {
        left = Math.max(
          0,
          Math.min(
            popupLeft + popupRect.width / 2 - modalWidth / 2,
            availableMapWidth - modalWidth
          )
        );
        top = popupSafeZone.top;
      }
      // Fallback - center in available space
      else {
        left = Math.max(0, availableMapWidth / 2 - modalWidth / 2);
        top = Math.max(0, mapRect.height / 2 - modalHeight / 2);
      }

      // Final boundary checks
      top = Math.max(0, Math.min(top, mapRect.height - modalHeight));
      left = Math.max(0, Math.min(left, availableMapWidth - modalWidth));

      setModalPosition({ top, left });
    }

    setSelectedItem((prev) => (prev === item ? null : item));
  };

  const createEmployeeMarker = (coord: Coordinate) => {
    const label = `
      <div>
        <strong>Исполнитель:</strong> ${
          coord.name || 'Неизвестный монтажник'
        }<br/>
        ${
          coord.lastCoordinateUpdate
            ? `<strong>Обновлено:</strong> ${formatDateStringFromRaw(
                coord.lastCoordinateUpdate
              )}<br/>`
            : ''
        }
        <button class="marker-btn cursor-pointer">Подробнее</button>
      </div>
    `;

    const marker = window.DG.marker([coord.latitude, coord.longitude], {
      icon: createPrimeIconMarker('#0000ff')
    }).bindPopup(label);

    marker.on('popupopen', () => {
      // Close any existing modal when new popup opens
      setSelectedItem(null);
      setModalPosition(null);

      const popupElement = marker.getPopup().getElement();
      popupElement
        ?.querySelector('.marker-btn')
        ?.addEventListener('click', (e: { stopPropagation: () => void }) => {
          e.stopPropagation();
          handleMarkerClick(coord, marker);
        });
    });

    return marker;
  };

  const createTaskMarker = (task: TaskCoordinate) => {
    const label = `
      <div>
        <div class="flex mr-3"><strong>Наряд: &nbsp; </strong>${
          task.id
        }</div><br/>
        <strong>Объект:</strong> ${task.asset?.name || 'Без названия'}<br/>
        <button class="marker-btn cursor-pointer">Информация</button>
      </div>
    `;

    const marker = window.DG.marker(
      [task.asset.latitude, task.asset.longitude],
      {
        icon: createPrimeIconMarker('#ff0000')
      }
    ).bindPopup(label);

    marker.on('popupopen', () => {
      // Close any existing modal when new popup opens
      setSelectedItem(null);
      setModalPosition(null);

      const popupElement = marker.getPopup().getElement();
      popupElement
        ?.querySelector('.marker-btn')
        ?.addEventListener('click', (e: { stopPropagation: () => void }) => {
          e.stopPropagation();
          handleMarkerClick(task, marker);
        });
    });

    return marker;
  };

  const createStatusTransitionMarker = (
    transition: TaskStatusTransitionCoordinate & {
      latitude: number;
      longitude: number;
    },
    order: number
  ) => {
    const label = `
      <div>
        <div><strong>${order}.</strong> ${
          transition.toStatus?.label || ''
        }</div>
        <button class="marker-btn cursor-pointer">Детали</button>
      </div>
    `;

    const marker = window.DG.marker(
      [transition.latitude, transition.longitude],
      {
        icon: createPrimeIconMarker('#00b894')
      }
    ).bindPopup(label);

    marker.on('popupopen', () => {
      setSelectedItem(null);
      setModalPosition(null);

      const popupElement = marker.getPopup().getElement();
      popupElement
        ?.querySelector('.marker-btn')
        ?.addEventListener('click', (e: { stopPropagation: () => void }) => {
          e.stopPropagation();
          handleMarkerClick(transition, marker);
        });
    });

    return marker;
  };

  const renderModalContent = () => {
    if (!selectedItem) return null;

    // For employees (Coordinate type)
    if ('personnelNumber' in selectedItem) {
      const data = selectedItem as Coordinate;
      return (
        <>
          <h3>{data.name || 'Неизвестный монтажник'}</h3>
          <p>
            <strong>Время обновления:</strong>{' '}
            {data.lastCoordinateUpdate
              ? formatDateStringFromRaw(data.lastCoordinateUpdate)
              : 'Нет данных'}
          </p>
          {data.departments?.length > 0 && (
            <p>
              <strong>Участки:</strong>{' '}
              {data.departments.map((dept) => dept.label).join(', ')}
            </p>
          )}
          {data.personnelNumber && (
            <p>
              <strong>Табельный номер:</strong> {data.personnelNumber}
            </p>
          )}
          {data.taskIds?.length > 0 && (
            <p>
              <strong>Наряды:</strong> {data.taskIds.join(', ')}
            </p>
          )}
        </>
      );
    }

    if ('fromStatus' in selectedItem && 'toStatus' in selectedItem) {
      const data = selectedItem as TaskStatusTransitionCoordinate;
      return (
        <>
          <h3>Переход статуса</h3>
          <p>
            <strong>Статус после:</strong> {data.toStatus?.label}
          </p>
          <p>
            <strong>Статус до:</strong> {data.fromStatus?.label}
          </p>
          {data.createdBy?.label && (
            <p>
              <strong>Исполнитель:</strong> {data.createdBy.label}
            </p>
          )}
          <p>
            <strong>Дата и время:</strong> {formatDateStringFromRaw(data.date)}
          </p>
          {data.latitude !== undefined && data.longitude !== undefined ? (
            <p>
              <strong>Координаты:</strong>{' '}
              {`${data.latitude}° N, ${data.longitude}° E`}
            </p>
          ) : (
            <p>
              <strong>Координаты:</strong> отсутствуют
            </p>
          )}
          {data.closingCode?.label && (
            <p>
              <strong>Код закрытия:</strong> {data.closingCode.label}
            </p>
          )}
        </>
      );
    }

    const data = selectedItem as TaskCoordinate;
    return (
      <>
        <h3>Наряд #{data.id}</h3>
        <p>
          <strong>Тип наряда:</strong> {data.taskType}
        </p>
        <p>
          <strong>Статус наряда:</strong> {data.taskStatus}
        </p>
        <p>
          <strong>Приоритет:</strong>{' '}
          {getPriorityName(data.priority as Priority)}
        </p>
        {data.employees?.length > 0 && (
          <p>
            <strong>Исполнители:</strong>{' '}
            {data.employees.map((e) => e.fullName).join(', ')}
          </p>
        )}
        <p>
          <strong>Название объекта:</strong>{' '}
          {data.asset?.name || 'Без названия'}
        </p>
        <p>
          <strong>Адрес:</strong> {data.asset?.address || 'Не указан'}
        </p>
        <p>
          <strong>Координаты:</strong>{' '}
          {data.asset?.latitude + '° N, ' + data.asset?.longitude + '° E'}
        </p>
        <p>
          <strong>Тип объекта:</strong> {data.asset?.assetClass || 'Не указан'}
        </p>
      </>
    );
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div id={containerId} style={{ width: '100%', height: '100%' }}></div>

      {selectedItem && modalPosition && (
        <div
          style={{
            position: 'absolute',
            top: `${modalPosition.top}px`,
            left: `${modalPosition.left}px`,
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            maxWidth: '400px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {renderModalContent()}
          <button
            style={{
              marginTop: '10px',
              padding: '5px 10px',
              background: '#64748B',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={() => {
              setSelectedItem(null);
              setModalPosition(null);
            }}
          >
            Закрыть
          </button>
        </div>
      )}

      <style>
        {`
          .task-cluster {
            background-color: rgba(255, 0, 0, 0.6);
            border-radius: 50%;
            text-align: center;
            color: white;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .employee-cluster {
            background-color: rgba(0, 0, 255, 0.6);
            border-radius: 50%;
            text-align: center;
            color: white;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}
      </style>
    </div>
  );
};

export default MapComponent;
